import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiRequest } from "../services/api";

const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const login = useCallback(async ({ identifier, password }) => {
    const response = await apiRequest("/customer-auth/login", {
      method: "POST",
      body: { identifier, password },
    });

    setAccessToken(response.accessToken);
    setCustomer(response.customer);

    return response.customer;
  }, []);

  const register = useCallback(async ({ fullName, email, phone, password }) => {
    const response = await apiRequest("/customer-auth/register", {
      method: "POST",
      body: { fullName, email, phone: phone || undefined, password },
    });

    setAccessToken(response.accessToken);
    setCustomer(response.customer);

    return response.customer;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiRequest("/customer-auth/logout", {
        method: "POST",
      });
    } finally {
      setAccessToken(null);
      setCustomer(null);
    }
  }, []);

  useEffect(() => {
    const restoreCustomerSession = async () => {
      try {
        const response = await apiRequest("/customer-auth/refresh", {
          method: "POST",
        });

        setAccessToken(response.accessToken);
        setCustomer(response.customer);
      } catch {
        setAccessToken(null);
        setCustomer(null);
      } finally {
        setIsLoading(false);
      }
    };

    restoreCustomerSession();
  }, []);

  const value = useMemo(
    () => ({
      accessToken,
      customer,
      isLoading,
      isAuthenticated: Boolean(accessToken && customer),
      login,
      register,
      logout,
    }),
    [accessToken, customer, isLoading, login, register, logout],
  );

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const context = useContext(CustomerAuthContext);

  if (!context) {
    throw new Error(
      "useCustomerAuth must be used inside CustomerAuthProvider",
    );
  }

  return context;
}